import {remCalc} from '_themeUtils'

const drawer = {
    Drawer: {
        baseStyle: {
            dialog: {},
            overlay: {
                zIndex: 'tooltip',
            },
            dialogContainer: {},
            header: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pt: 0,
                pb: 'spacing.10',
                px: 0,
                borderBottom: '1px solid',
                borderColor: 'color.border.drawer.default',
                p: {
                    color: 'color.text.drawer.default',
                },
            },
            titleRow: {
                w: '100%',
                alignItems: 'center',
            },
            body: {
                px: 0,
                py: 0,
            },
            backIcon: {},
            closeIcon: {
                alignSelf: 'flex-start',
            },
            footer: {
                px: 0,
                pt: 0,
            },
        },
        sizes: {
            lg: {
                dialog: {
                    maxW: {base: '100%', md: '50%', xl: '45%'},
                    pt: {base: 'spacing.8', md: 'spacing.10', xl: 'spacing.10'},
                    pb: 'spacing.8',
                    px: {base: 'spacing.4', md: 'spacing.6', xl: 'spacing.10'},
                },
                header: {
                    pt: 0,
                    pb: {base: 'spacing.8', md: 'spacing.10', xl: 'spacing.10'},
                    px: 0,
                    borderBottom: '1px solid',
                    borderColor: 'color.border.drawer.default',
                    p: {
                        textStyle: 'paragraphMd',
                        textTransform: 'uppercase',
                        fontWeight: 300,
                        letterSpacing: remCalc(4),
                    },
                },
                body: {
                    mx: {base: '-spacing.4', md: '-spacing.6', xl: '-spacing.10'},
                    px: {base: 'spacing.4', md: 'spacing.6', xl: 'spacing.10'},
                },
                footer: {
                    pb: 0,
                },
            },
            md: {
                dialog: {
                    maxW: {base: '100%', lg: '45%', xl: '35%'},
                    p: 'spacing.10',
                },
                header: {
                    p: {
                        textStyle: 'paragraphMd',
                        textTransform: 'uppercase',
                        fontWeight: 400,
                        letterSpacing: remCalc(1),
                    },
                },
            },
        },
        variants: {
            sortBy: {
                header: {
                    pb: 'spacing.3.5',
                    p: {
                        w: '100%',
                        textAlign: 'center',
                        textStyle: 'displayXs',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        fontWeight: 400,
                        ml: 'spacing.8',
                    },
                },
                dialog: {
                    maxW: '100%',
                    h: '100dvh',
                    p: 'spacing.3.5',
                },
            },
            logIn: {
                dialog: {
                    overflow: 'auto',
                    maxH: '100vh',
                },
                header: {
                    pb: 'spacing.16',
                    border: 'none',
                    p: {
                        letterSpacing: '4px',
                    },
                },
                body: {
                    overflow: 'unset',
                },
            },
            smallHeader: {
                header: {
                    pb: 'spacing.6',
                    border: 'none',
                    p: {
                        textStyle: {base: 'displayXs', lg: 'displaySm'},
                        textTransform: 'unset',
                        fontWeight: 300,
                        letterSpacing: 'unset',
                    },
                },
                dialog: {
                    maxW: {base: '100%', lg: '50%'},
                    pt: 'spacing.8',
                    pb: '0',
                    px: 'spacing.8',
                },
            },
            shopTheImage: {
                header: {
                    pb: 'spacing.6',
                    border: 'none',
                    pt: {
                        base: 'env(safe-area-inset-top)',
                        md: 'spacing.8',
                        lg: 'spacing.8',
                    },
                    p: {
                        textStyle: {base: 'displayXs', lg: 'displaySm'},
                        textTransform: 'unset',
                        fontWeight: 300,
                        letterSpacing: 'unset',
                    },
                },
                titleRow: {
                    pt: 0,
                },
                dialog: {
                    maxW: {base: '100%', md: '100%', lg: '100%', xl: '50%'},
                    pt: 'spacing.8',
                    pb: '0',
                    px: 'spacing.8',
                },
            },
            listingFilters: {
                header: {
                    justifyContent: 'center',
                    mx: {base: 0, md: '-spacing.6', xl: '-spacing.10'},
                    px: {base: 0, md: 'spacing.6', xl: 'spacing.10'},
                },
                body: {
                    px: {base: 'spacing.4', md: 0, xl: 0},
                },
                footer: {
                    justifyContent: 'center',
                    mt: 'spacing.8',
                },
            },
            noBorder: {
                header: {
                    borderBottom: 'none',
                },
                // The lg size adds a negative body margin (mx: -spacing.4 …) + matching px to
                // bleed content full-width. Combined with the old pl/pr: spacing.6 that offset
                // the body content inward from the header, so the title sat further left than the
                // form (Figma 58195-11032 wants them flush). Reset the body to share the dialog's
                // padding edge with the header (mx: 0, px: 0).
                body: {
                    mx: 0,
                    px: 0,
                },
            },
            minicart: {
                dialog: {
                    maxW: {base: '100%', md: '50%', xl: '45%'},
                    pt: {base: 'spacing.8', md: 'spacing.10'},
                    pb: {base: '0', md: 'spacing.10'},
                    px: {base: 'spacing.4', md: 'spacing.6', xl: 'spacing.6'},
                },
                header: {
                    pb: {base: 'spacing.8', md: 'spacing.10'},
                    p: {
                        textStyle: 'paragraphMd',
                        textTransform: 'uppercase',
                        fontWeight: 300,
                        letterSpacing: remCalc(4),
                    },
                },
                body: {
                    mx: {base: '-spacing.4', md: '-spacing.6', xl: '-spacing.6'},
                    px: {base: 'spacing.4', md: 'spacing.6', xl: 'spacing.6'},
                },
            },
            tabs: {
                dialog: {
                    display: 'flex',
                    flexDirection: {base: 'column', md: 'row-reverse'},
                    // Figma: drawer padding is 24px (mobile) / 60px (desktop) horizontal, 30px top,
                    // 50px (mobile) / 60px (desktop) bottom (5847-11406 / 5655-4321 details,
                    // 5847-11290 / 5655-4198 size guide). Overrides the shared sizes.lg padding so the
                    // content sits flush to the design edges without extra body px.
                    pt: remCalc(30),
                    pb: {base: remCalc(50), md: remCalc(50), xl: remCalc(60)},
                    px: {base: 'spacing.6', md: 'spacing.6', xl: 'spacing.15'},
                },
                header: {
                    borderBottom: 'none',
                    pb: 0,
                    alignSelf: {base: 'flex-end', md: 'flex-start'},
                },
                body: {
                    mx: 0,
                    px: 0,
                    pt: {base: 'spacing.5', md: '0'},
                },
            },
        },
    },
}

export default drawer
